import React, { useContext, useEffect, useState } from 'react';
import kebabCase from 'lodash/kebabCase';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import Carousel from 'tbk-components/src/components/Carousel';
import CarouselItem from 'tbk-components/src/components/CarouselItem';
import Blockquote from 'tbk-components/src/components/Blockquote';
import Image, { IImage } from 'tbk-components/src/components/Image';
import withAppContext from 'tbk-components/src/hooks/withAppContext';
import Loading from 'tbk-components/src/components/Loading';

/**
 * @useTermData
 * @taxonomy testimonial_category
 * @forceLinkedData
 */
export interface ITestimonialCategory {
	term_id?: number;
	name?: string;
}

/**
 * @usePostData
 * @post_type testimonial
 * @forceLinkedData
 */
export interface ITestimonial {
	/**
	 * @control textarea
	 */
	text?: string;
	authorImage?: IImage;
	authorName?: string;
	authorCompany?: string;
}

export interface ITestimonialCarouselProps extends IBaseElementProps {
	/**
	 * @noUI
	 */
	context;
	/**
	 * Theme - Default: Light
	 */
	theme?: 'Light' | 'Dark';
	/**
	 * Choose a category to filter by, or leave blank for all testimonials
	 */
	filterByCategory?: ITestimonialCategory[];
	testimonials?: ITestimonial[];
	/**
	 * @control uniqueid
	 * @prefix testimonial-carousel
	 */
	id?: string;
}

/**
 * Calian Testimonial Carousel Component
 * @block
 * @icon text
 */
const TestimonialCarousel: React.FC<ITestimonialCarouselProps> = withAppContext(
	({
		context,
		className,
		classNames = [],
		theme = 'Light',
		filterByCategory,
		testimonials = [],
		id,
	}) => {
		//@ts-ignore
		const { Api } = useContext(context);
		const [displayTestimonials, setDisplayTestimonials] = useState<
			ITestimonialCarouselProps['testimonials']
		>([]);
		const [testimonialCount, setTestimonialCount] = useState<number>(0);
		const [loading, setLoading] = useState<boolean>(false);

		useEffect(() => {
			const processedTestimonials =
				testimonials == null ? [] : [...testimonials];

			if (processedTestimonials.length === 0) {
				processedTestimonials.push(null);
			}

			setLoading(true);
			Api.populateTestimonials({
				testimonials: processedTestimonials,
				categories: filterByCategory,
			})
				.then((results) => {
					if (results.length === 0) {
						setDisplayTestimonials(testimonials);
						setTestimonialCount(testimonials.length);
					} else {
						setDisplayTestimonials(results);
						setTestimonialCount(results.length);
					}
				})
				.finally(() => {
					setLoading(false);
				});
		}, [JSON.stringify(testimonials)]);

		if (loading) {
			return <Loading />;
		}

		const testimonialRender = (
			testimonial: ITestimonial,
			index: number,
		) => {
			return (
				<div
					className={
						className ||
						cn(
							'calian-testimonial-item calian-testimonial-carousel__item-inner',
							...classNames,
						)
					}
				>
					{testimonial.authorImage && (
						<div className="calian-testimonial-carousel__item--author-image text-center min-width-190px">
							<Image
								className="img-fluid mb-4"
								{...testimonial.authorImage}
							/>
							{testimonial.authorName && (
								<p className="h5 mb-1">
									{testimonial.authorName}
								</p>
							)}
							{testimonial.authorCompany && (
								<p>{testimonial.authorCompany}</p>
							)}
						</div>
					)}
					<Blockquote
						className="calian-testimonial-carousel__item mw-70 mw-lg-762px ps-lg-7 mx-auto"
						children={testimonial.text}
						author={testimonial.authorName}
						source={testimonial.authorCompany}
					/>
				</div>
			);
		};

		return (
			<div
				className={cn('calian-testimonial-carousel', {
					[`theme-${kebabCase(theme)}`]: theme,
					[`has-author-image`]: displayTestimonials?.some(
						(testimonial) => testimonial.authorImage,
					),
				})}
			>
				{displayTestimonials && (
					<div className="calian-testimonial-carousel__container position-relative container-fluid mw-1440px">
						{displayTestimonials?.length > 1 ? (
							<Carousel showArrows showBullets id={id}>
								{(displayTestimonials || []).map(
									(testimonial, index) => (
										<CarouselItem key={index}>
											{testimonialRender(
												testimonial,
												index,
											)}
										</CarouselItem>
									),
								)}
							</Carousel>
						) : displayTestimonials?.length == 1 ? (
							testimonialRender(displayTestimonials[0], 0)
						) : (
							<div className="calian-testimonial-carousel__item">
								No testimonials found
							</div>
						)}
					</div>
				)}
			</div>
		);
	},
);

export default TestimonialCarousel;
